import apiClient from './client'

export interface BacktestMetrics {
  initialCapital: number
  finalCapital: number
  totalReturn: number
  totalTrades: number
  winningTrades: number
  losingTrades: number
  winRate: number
  profitFactor: number
  maxDrawdown: number
  sharpeRatio: number | null
  sortinoRatio: number | null
  calmarRatio: number | null
}

export interface Trade {
  id: string
  entryTimestamp: number
  exitTimestamp: number
  direction: 'long' | 'short'
  entryPrice: number
  exitPrice: number
  pnl: number
  pnlPercent: number
  exitReason: string
  signalName: string
  duration: number
  size: number
  symbol?: string
}

export interface EquityCurvePoint {
  timestamp: number
  equity: number
}

export interface BacktestResult {
  id: string
  jobId: string
  metrics: BacktestMetrics
  trades: Trade[]
  equityCurve: EquityCurvePoint[]
  diagnostics?: {
    signalsGenerated?: number
    signalsMapped?: number
    candlesProcessed?: number
    candlesSkipped?: number
    candlesWithSignals?: number
    sqlColumns?: string[]
    configuredSignals?: {
      long?: string[]
      short?: string[]
    }
    booleanColumns?: string[]
  }
  createdAt: string
}

export interface BacktestResponse {
  success: boolean
  backtest: BacktestResult
}

export const backtestApi = {
  get: async (jobId: string): Promise<BacktestResult> => {
    const response = await apiClient.get<BacktestResponse>(`/backtest/${jobId}`)
    if (!response.data.success || !response.data.backtest) {
      throw new Error('Backtest result not found')
    }
    return response.data.backtest
  },

  getMetrics: async (jobId: string): Promise<BacktestMetrics> => {
    const response = await apiClient.get<{ success: boolean; metrics: BacktestMetrics }>(`/backtest/${jobId}/metrics`)
    if (!response.data.success || !response.data.metrics) {
      throw new Error('Backtest metrics not found')
    }
    return response.data.metrics
  },

  getTrades: async (jobId: string, options?: { limit?: number; offset?: number; direction?: 'long' | 'short' }): Promise<{ trades: Trade[]; pagination: { total: number; limit: number; offset: number; hasMore: boolean } }> => {
    const params = new URLSearchParams()
    if (options?.limit) params.append('limit', options.limit.toString())
    if (options?.offset) params.append('offset', options.offset.toString())
    if (options?.direction) params.append('direction', options.direction)
    
    const response = await apiClient.get<{ success: boolean; trades: Trade[]; pagination: any }>(`/backtest/${jobId}/trades?${params.toString()}`)
    if (!response.data.success) {
      throw new Error('Backtest trades not found')
    }
    return {
      trades: response.data.trades,
      pagination: response.data.pagination,
    }
  },

  getEquityCurve: async (jobId: string, limit?: number): Promise<{ equityCurve: EquityCurvePoint[]; totalPoints: number }> => {
    const params = new URLSearchParams()
    if (limit) params.append('limit', limit.toString())
    
    const response = await apiClient.get<{ success: boolean; equityCurve: EquityCurvePoint[]; totalPoints: number }>(`/backtest/${jobId}/equity-curve?${params.toString()}`)
    if (!response.data.success) {
      throw new Error('Backtest equity curve not found')
    }
    return {
      equityCurve: response.data.equityCurve,
      totalPoints: response.data.totalPoints,
    }
  },
}
